import axios from 'axios';
import type { AiProviderErrorRaw, NavigationOffer } from '@/types/ai/cleoChat';
import { useAuthStore } from '@/stores/auth';

const agentBaseUrl = import.meta.env.VITE_AGENT_URL ?? 'http://localhost:5000';

// The agent keeps forwarding the SAME token to the backend for every tool call of the
// whole multi-minute turn — a refresh elsewhere in the SPA revokes it mid-turn, so a
// stream must start with a comfortable remaining lifetime, not just the 30s floor.
const STREAM_TOKEN_MIN_REMAINING_MS = 5 * 60_000;

const agentHttp = axios.create({
    baseURL: agentBaseUrl,
    headers: { 'Content-Type': 'application/json' },
    timeout: 120_000,
});

agentHttp.interceptors.request.use(async (config) => {
    const authStore = useAuthStore();
    await authStore.ensureFreshToken();
    if (authStore.token) {
        config.headers['Authorization'] = `Bearer ${authStore.token}`;
    }
    return config;
});

agentHttp.interceptors.response.use(undefined, async (error) => {
    const config = error?.config as (typeof error.config & { _authRetry?: boolean }) | undefined;
    if (error?.response?.status === 401 && config && !config._authRetry) {
        config._authRetry = true;
        const authStore = useAuthStore();
        if (await authStore.refreshAccessToken()) {
            config.headers['Authorization'] = `Bearer ${authStore.token}`;
            return agentHttp.request(config);
        }
    }
    return Promise.reject(error);
});

const buildStreamHeaders = async (): Promise<HeadersInit> => {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    const authStore = useAuthStore();
    await authStore.ensureFreshToken(STREAM_TOKEN_MIN_REMAINING_MS);
    if (authStore.token) {
        headers['Authorization'] = `Bearer ${authStore.token}`;
    }
    return headers;
};

export interface TraceEventData {
    event: 'tool_call' | 'tool_result' | 'tool_error';
    plugin: string;
    function: string;
    args?: Record<string, string>;
    result?: string;
    error?: string;
}

export interface NavigationOfferEventData {
    options: NavigationOffer[];
}

export interface UsageEventData {
    model: string;
    input_tokens: number;
    output_tokens: number;
    cached_tokens: number;
}

export interface StreamEvent {
    type: 'start' | 'chunk' | 'done' | 'trace' | 'navigation_offer' | 'usage' | 'title' | 'thread_expired' | 'ai_provider_error' | 'quota_exceeded';
    conversationId?: string;
    content?: string;
    data?: TraceEventData | NavigationOfferEventData | UsageEventData;
    title?: string;
    code?: string;
    message?: string;
    retryable?: boolean;
    retry_after_seconds?: number | null;
    raw?: AiProviderErrorRaw;
    limit?: number | null;
    used?: number;
    period?: string | null;
    reset_at?: string | null;
}

export async function* streamMessage(
    message: string,
    conversationId?: string,
    mood?: string,
    wantTitle = false,
): AsyncGenerator<StreamEvent> {
    const body = JSON.stringify({ message, conversationId: conversationId ?? null, mood: mood ?? null, wantTitle });
    let response = await fetch(`${agentBaseUrl}/api/chat/stream`, {
        method: 'POST',
        headers: await buildStreamHeaders(),
        body,
    });

    // 401 means the agent rejected the token before doing any work (revoked by a refresh
    // elsewhere) — safe to retry the same message once with a fresh token.
    if (response.status === 401) {
        const authStore = useAuthStore();
        if (await authStore.refreshAccessToken()) {
            response = await fetch(`${agentBaseUrl}/api/chat/stream`, {
                method: 'POST',
                headers: await buildStreamHeaders(),
                body,
            });
        }
    }

    if (!response.ok || !response.body) {
        throw new Error(`Agent error: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';

            for (const part of parts) {
                for (const line of part.split('\n')) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6).trim();
                        if (data) {
                            yield JSON.parse(data) as StreamEvent;
                        }
                    }
                }
            }
        }
    } finally {
        reader.cancel().catch(() => {});
    }
}

export const clearConversation = async (conversationId: string): Promise<void> => {
    await agentHttp.delete(`/api/chat/${conversationId}`);
};

export const restoreConversation = async (
    messages: Array<{ role: 'user' | 'assistant'; content: string }>,
): Promise<string> => {
    const { data } = await agentHttp.post<{ conversationId: string }>('/api/chat/restore', {
        messages,
    });
    return data.conversationId;
};

export interface GreetApiResponse {
    message: string;
    conversationId: string;
}

export const greetUser = async (mood?: string): Promise<GreetApiResponse> => {
    const firstName = useAuthStore().user?.first_name ?? null;
    const { data } = await agentHttp.post<GreetApiResponse>('/api/chat/greet', {
        mood: mood ?? null,
        firstName,
    });
    return data;
};

